// App-icon badge for installed PWAs (iOS 16.4+ home-screen apps, Android,
// desktop Chrome). While the app is open the page is the source of truth:
// AppShell syncs the real notifiable-unread total here. The count is also
// persisted to IndexedDB so the service worker can increment from the right
// base for pushes that arrive while the app is closed (sw.js opens the same
// DB — keep the names in sync with it).

const DB_NAME = 'chatapp-badge';
const STORE = 'kv';

type BadgeNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function persistCount(count: number): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(count, 'count');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function syncAppBadge(count: number): Promise<void> {
  const nav = navigator as BadgeNavigator;
  if (!nav.setAppBadge) return; // unsupported browser — silently no-op
  try {
    if (count > 0) await nav.setAppBadge(count);
    else await nav.clearAppBadge?.();
    await persistCount(count);
  } catch {
    // Badge is best-effort decoration; never let it break the app.
  }
}
